<template>
    <div id="settingsService">
        <section v-if="doneLoading">
            <h2 id="title">Service information</h2>

            <div v-for="service in services" :key="service.identification" class="service-info-wrapper">
                <h3><span id="name">{{service.name}}</span></h3>
                <p>
                    <custom-input
                        class="identification"
                        label="Identification"
                        type="display"
                        name="identification"
                        v-model="service.identification"/>
                </p>
                <p>
                    <custom-input
                        class="webhookEndpoint"
                        label="Webhook endpoint"
                        type="display"
                        name="webhookEndpoint"
                        id="webhook-endpoint"
                        v-model="service.callbackUrl"/>
                    <help-popover>
                        <template slot="content">
                            This is the endpoint to which all the messages are send
                            <br><br>
                            <a href="https://developers.parley.nu/docs/setting-up-a-service" target="_blank">
                                More info
                            </a>
                        </template>
                    </help-popover>
                </p>
                <p>
                    <custom-input
                        class="sharedSecret"
                        label="Shared service account secret"
                        name="sharedServiceAccountSecret"
                        type="secret"
                        v-model="service.sharedServiceAccountSecret"
                        :showSecret="service.showSecret"
                        v-on:toggleSecret="toggleSecretDisplay(service)"
                    />
                    <help-popover>
                        <template slot="content">
                            This secret is used in creating the Authorization string, which you'll need
                            to authenticate yourself with the Service API.
                            <br><br>
                            <a href="https://developers.parley.nu/docs/service-api-authorization" target="_blank">
                                More info
                            </a>
                        </template>
                    </help-popover>
                </p>
                <p>
                    <custom-input label="Is default service" name="defaultService" type="toggle" :disabled="isSavingDefaultService" v-model="service.isDefault" @change="changeDefaultServiceState($event, service)"/>
                    <help-popover>
                        <template slot="content">
                            This toggle will set the Service as <b>default</b> for this account.
                            <br><br>
                            A default Service will gain ownership of every <b>new</b> User that uses the chat.
                            With this ownership the Service is allowed to send messages to that User.
                            <br><br>
                            An Account can only have 1 Service as <b>default</b>.
                            <br><br>
                            <a href="https://developers.parley.nu/docs/handover-protocol" target="_blank">
                                More info
                            </a>
                        </template>
                    </help-popover>
                </p>

                <section name="service-queue-info">
                    <h4>Service queue</h4>
                    <p>
                        Next retry: <span id="nextRetry">{{formatTimestamp(service.nextRetry) || '-'}}</span>
                        <help-popover>
                            <template slot="content">
                                This is the time at which the Service Queue will be read and all messages will be
                                send to the Service.
                                <br><br>
                                <a href="https://developers.parley.nu/docs/service-queue" target="_blank">
                                    More info
                                </a>
                            </template>
                        </help-popover>
                    </p>
                    <p>
                        Attempts: <span id="attempts">{{service.attempts}}</span>
                        <help-popover>
                            <template slot="content">
                                This number indicates how many <b>new</b> messages have not been received by
                                the Service, due to an error on the Service's side.
                                <br><br>
                                <a href="https://developers.parley.nu/docs/service-queue" target="_blank">
                                    More info
                                </a>
                            </template>
                        </help-popover>
                    </p>
                    <p>
                        Retries: <span id="retries">{{service.retries}}</span>
                        <help-popover>
                            <template slot="content">
                                This number indicates how many <b>queued</b> messages have been retried.
                                <br><br>
                                Note; there is a threshold on retries. If the retries matches this threshold
                                the Service will be disabled, meaning that we won't send <b>queued</b> messages
                                to this Service.
                                <br><br>
                                To re-enable it, use the "Reset queue" button below.
                                <br><br>
                                <a href="https://developers.parley.nu/docs/service-queue" target="_blank">
                                    More info
                                </a>
                            </template>
                        </help-popover>
                    </p>

                    <button class="primary small" @click="resetCountersButton(service)">Reset queue</button>
                </section>
            </div>
        </section>

        <p v-if="showLoading && !doneLoading">
            Loading service information...
        </p>
    </div>
</template>

<script>
import { Default } from '@/mixins/mixins';
import CustomInput from '../additional/customInput';
import HelpPopover from '../additional/helpPopover';

export default {
    name: 'settingsService',
    components: {
        CustomInput,
        HelpPopover
    },
    mixins: [
        Default
    ],
    data() {
        return {
            showLoading: false,
            doneLoading: false,
            isSavingDefaultService: false,
            dummySecret: 'fakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefakefake',
            services: []
        };
    },
    async created() {
        // Only show loading when it takes longer than X ms
        window.setTimeout(() => {
            this.showLoading = true;
        }, 500);

        this.services = await this.getServicesInformation();
        this.sharedServiceAccountSecret = this.dummySecret;

        this.doneLoading = true;
    },
    methods: {
        getServicesInformation() {
            return new Promise((resolve) => {
                return this.$http.get('v1.1/services/account', {}).then(response => {
                    // Success callback
                    let services = response.data.data.services;
                    services = services.map(service => {
                        return {
                            ...service,
                            sharedServiceAccountSecret: this.dummySecret
                        };
                    });

                    resolve(services);
                }, () => {
                    // Error callback
                    this.$parleyAdmin.queryErrors.addError('Something went wrong while retrieving the service information, please try again later');
                    resolve(false);
                });
            });
        },
        getServiceSecret(serviceIdentification) {
            return new Promise((resolve) => {
                return this.$http.get('v1.1/services/account/sharedServiceAccountSecret/' + serviceIdentification, {}).then(response => {
                    // Success callback
                    const service = response.data.data.service;

                    resolve(service.sharedServiceAccountSecret);
                }, () => {
                    // Error callback
                    this.$parleyAdmin.queryErrors.addError('Something went wrong while retrieving the service secret, please try again later');
                    resolve(false);
                });
            });
        },
        async toggleSecretDisplay(service) {
            const serviceIndex = this.services.indexOf(service);

            if (this.services[serviceIndex].showSecret) {
                this.services[serviceIndex].showSecret = false;
                this.services[serviceIndex].sharedServiceAccountSecret = this.dummySecret;
            } else {
                this.services[serviceIndex].showSecret = true;
                this.services[serviceIndex].sharedServiceAccountSecret = "loading...";
                this.services[serviceIndex].sharedServiceAccountSecret = await this.getServiceSecret(service.identification);
            }
        },
        resetCountersButton(service) {
            this.resetCounters(service)
                .then(async () => {
                    this.services = await this.getServicesInformation();
                });
        },
        resetCounters(service) {
            return new Promise((resolve) => {
                return this.$http.post('v1.1/services/account/resetQueue/'+service.identification, {}).then(response => {
                    // Success callback
                    if(response.data.notifications.filter(notification => {return notification.message === "queue_reset";}).length > 0) {
                        this.$parleyAdmin.queryErrors.addError('Successfully reset the queue', this.$parleyAdmin.queryErrors.TYPE_SUCCESS);
                        resolve(true);
                    } else {
                        this.$parleyAdmin.queryErrors.addError('Something went wrong while resetting the queue, please try again later');
                        resolve(false);
                    }
                }, () => {
                    // Error callback
                    this.$parleyAdmin.queryErrors.addError('Something went wrong while resetting the queue, please try again later');
                    resolve(false);
                });
            });
        },
        changeDefaultServiceState(event, service) {
            const isNewDefault = event.target.checked;

            if(!isNewDefault) {
                const serviceIndex = this.services.findIndex(_service => {return _service.identification === service.identification;});
                this.$nextTick(() => { // Resetting the isDefault param (and thus the toggle) can only happen after the tick in which the toggle was changing originally
                    this.services[serviceIndex].isDefault = true;
                });
                this.$parleyAdmin.queryErrors.addError('At least 1 service must be the default, enable a different service if you want to disable this one', this.$parleyAdmin.queryErrors.TYPE_INFO);
                return; // We dont have to do anything if you remove a default service
            }

            // Disable all toggles until we get a response from the server
            this.isSavingDefaultService = true;

            return this.$http.put('v1.1/services/account/default', {
                identification: service.identification
            }).then(() => {
                // Success callback
                this.$parleyAdmin.queryErrors.addError('The default service has been updated', this.$parleyAdmin.queryErrors.TYPE_SUCCESS);
                if(this.services.length > 1) {
                    for (let i = 0; i < this.services.length; i++) { // Because we edit the collection, we cannot use a `forEach`
                        if(this.services[i].identification === service.identification) {
                            continue; // Ignore the current service
                        }
                        this.services[i].isDefault = false;
                    }
                }
            }, response => {
                // Error callback
                if(response.data.notifications.filter(notification => {return notification.message === "service_is_already_default";}).length > 0) {
                    this.$parleyAdmin.queryErrors.addError('This service is already the default', this.$parleyAdmin.queryErrors.TYPE_INFO);
                } else {
                    this.$parleyAdmin.queryErrors.addError('Something went wrong while saving the default service, please try again later');
                }
            }).then(() => {
                this.isSavingDefaultService = false;
            });
        },
        formatTimestamp(timestamp) {
            if(!timestamp) {
                return false;
            }

            return new Intl.DateTimeFormat('nl-NL', {
                year: 'numeric', month: 'numeric', day: 'numeric',
                hour: 'numeric', minute: 'numeric', second: 'numeric'
            }).format(new Date(timestamp * 1000)); // *1000 because Date() works in ms
        }
    }
};
</script>

<style lang="scss" scoped>
    @import "../../assets/styles/variables";

    #settingsService {
        width: auto;
        overflow-wrap: anywhere;

        section {
            margin-bottom: 1em;
        }

        p {
            span {
                color: $secondary-color;
            }
        }

        h4 {
            padding: 0;
        }

        button.primary.small {
            margin-top: .5em
        }

        section[name="service-queue-info"] {
            margin-top: 1em;
        }

        .service-info-wrapper {
            margin-bottom: 2em;
        }

        .custom-input {
            display: inline-block;
            padding: 0;

            &.webhookEndpoint,
            &.sharedSecret {
                width: 39.5em;
            }

            &.toggle {
                position: relative;
                top: 5px;
            }
        }

        [name="v-popover"] {
            vertical-align: top;
            margin-top: 0.3rem;
            display: inline-block;
        }
    }
</style>
